import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

type TipoAviso = 'exito' | 'error' | 'info';

interface Aviso {
  id: number;
  tipo: TipoAviso;
  texto: string;
}

interface ContextoAvisos {
  mostrar: (texto: string, tipo?: TipoAviso) => void;
  exito: (texto: string) => void;
  error: (texto: string) => void;
}

const Contexto = createContext<ContextoAvisos | null>(null);

const ICONOS: Record<TipoAviso, ReactNode> = {
  exito: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  ),
  error: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-red)" strokeWidth="2.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16h.01" />
    </svg>
  ),
  info: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent-blue)" strokeWidth="2.5" strokeLinecap="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 16v-5M12 8h.01" />
    </svg>
  ),
};

export function ProveedorAvisos({ children }: { children: ReactNode }) {
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const siguienteId = useRef(1);

  const mostrar = useCallback((texto: string, tipo: TipoAviso = 'exito') => {
    const id = siguienteId.current++;
    setAvisos((previos) => [...previos, { id, tipo, texto }]);

    // Los errores se quedan más rato: suelen decir qué hay que corregir, y
    // 2,6 segundos no alcanzan para leer un mensaje de validación.
    setTimeout(() => {
      setAvisos((previos) => previos.filter((aviso) => aviso.id !== id));
    }, tipo === 'error' ? 6500 : 3200);
  }, []);

  const valor = useMemo<ContextoAvisos>(
    () => ({
      mostrar,
      exito: (texto: string) => mostrar(texto, 'exito'),
      error: (texto: string) => mostrar(texto, 'error'),
    }),
    [mostrar],
  );

  return (
    <Contexto.Provider value={valor}>
      {children}
      <div className="toast-container" role="status" aria-live="polite">
        {avisos.map((aviso) => (
          <div key={aviso.id} className={`toast ${aviso.tipo}`}>
            {ICONOS[aviso.tipo]}
            <span>{aviso.texto}</span>
          </div>
        ))}
      </div>
    </Contexto.Provider>
  );
}

export function useAvisos(): ContextoAvisos {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useAvisos debe usarse dentro de <ProveedorAvisos>.');
  return contexto;
}
